/**
 * ListLoader
 * 一覧系（ノウハウ/お知らせ/事例/セミナー）共通のデータローダー。
 * MT.net の「一度に100件まで」制限を回避するため、MTが年ごとに出力する
 * `{type}_{year}.json`（各 {items:[…]} ・各≤100件）を window.yearRange 分まとめて取得し、
 * items を結合して返す。404は無視（存在しない年はスキップ）。basePath+type+years でキャッシュ。
 *
 * 区切りは暦年が既定。年度（4-3月）へ切替が来た場合は、呼び出し側で years（=window.yearRange）に
 * 対象年を渡す／本クラスの fallbackYears を差し替える形で吸収する（itemの年計算には依存しない）。
 *
 * 設計：.claude/docs/forcle_design/coding/15_list_system.md
 */
export default class ListLoader {
  /**
   * @param {Object} opts
   * @param {string} opts.basePath ベースパス（例 '/blog/'）
   * @param {string} opts.type ファイル接頭辞（例 'blog' → blog_{year}.json）
   * @param {number[]} [opts.years] 読み込む年（既定 window.yearRange）
   * @returns {Promise<Array>} 結合済み items 配列
   */
  static async load({ basePath, type, years, onPartial } = {}) {
    const yrs = (Array.isArray(years) && years.length)
      ? years
      : (Array.isArray(window.yearRange) && window.yearRange.length ? window.yearRange : this.fallbackYears());

    const key = `${basePath}|${type}|${yrs.join(',')}`;

    if (this.cache.has(key)) {
      return this.cache.get(key);
    }
    if (this.loading.has(key)) {
      return this.loading.get(key);
    }

    const promise = this.fetchAll(basePath, type, yrs, onPartial);
    this.loading.set(key, promise);
    try {
      const data = await promise;
      this.cache.set(key, data);
      return data;
    } finally {
      this.loading.delete(key);
    }
  }

  /**
   * 全年を並列で取得しつつ、onPartial には「新しい年から連続して確定した分」だけを渡す。
   * 1本でも応答が遅いと全体が待たされるため、先に届いた新しい年で描画を始められるようにする
   * （年跨ぎで日付が逆転しないので、上位年が揃った時点の並びは確定扱いにできる）。
   */
  static async fetchAll(basePath, type, years, onPartial) {
    const desc = [...years].sort((a, b) => b - a);
    const requests = desc.map((year) => this.fetchOne(`${basePath}${type}_${year}.json`));

    if (typeof onPartial === 'function') {
      (async () => {
        const acc = [];
        for (const request of requests) {
          const data = await request;
          const items = (data && data.items) || [];
          if (items.length) acc.push(...items);
          onPartial(acc.slice());
        }
      })();
    }

    const results = await Promise.allSettled(requests);
    return results
      .filter((r) => r.status === 'fulfilled')
      .flatMap((r) => (r.value && r.value.items) || []);
  }

  static async fetchOne(url) {
    try {
      const res = await fetch(url);
      // 404＝その年のファイルが無いだけ（正常）。空で返す
      if (!res.ok) return { items: [] };
      return await res.json();
    } catch (e) {
      return { items: [] };
    }
  }

  /**
   * window.yearRange 未指定時のフォールバック（暦年・現在+1〜10年前を総当たり）。
   * 404許容なので無駄打ちは安全。年が増える運用では window.yearRange の注入を推奨。
   */
  static fallbackYears() {
    const now = new Date().getFullYear();
    const years = [];
    for (let y = now + 1; y >= now - 10; y -= 1) years.push(y);
    return years;
  }

  static clearCache() {
    this.cache.clear();
    this.loading.clear();
  }
}

// 静的プロパティ（ビルド互換のためクラス外で初期化）
ListLoader.cache = new Map();
ListLoader.loading = new Map();
